import { useNavigate } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import "./Help.css";
import TallyLogo from "../assets/Tally_Logotype_RGB_Brown.png";

export default function Help() {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <div className="help-container">
      <img
        src={TallyLogo}
        alt="Tally Logo"
        className="viewport-logo"
        onClick={handleLogoClick}
      />

      <header>
        <h1>
          <Trans context="Help page title">Help &amp; Documentation</Trans>
        </h1>
        <p className="intro">
          <Trans context="Help page introductory subtitle">
            Everything you need to know to get started with Tally and get the
            most out of it.
          </Trans>
        </p>
      </header>

      <section>
        <h2>
          <Trans context="Help page section heading: what is Tally">
            What is Tally?
          </Trans>
        </h2>
        <p>
          <Trans context="Help page: description of what Tally is">
            Tally is a chat-based financial data analysis tool that helps you
            understand your business data. It specializes in financial reporting
            and analysis, giving you insights into expenses, revenues, profits,
            losses, customers, and more &mdash; all by simply asking questions
            in plain language.
          </Trans>
        </p>
      </section>

      <section>
        <h2>
          <Trans context="Help page section heading: how it works">
            How does it work?
          </Trans>
        </h2>
        <p>
          <Trans context="Help page: high level explanation of how Tally works">
            You connect Tally to the business systems you already use. Tally
            syncs a copy of your data from those systems and then answers your
            questions by querying that synced data directly &mdash; it never
            writes back to or modifies your data in the original system. You can
            also generate dashboards from any answer to track key metrics over
            time.
          </Trans>
        </p>
      </section>

      <section>
        <h2>
          <Trans context="Help page section heading: integrations">
            Integrations
          </Trans>
        </h2>
        <p>
          <Trans context="Help page: introduction to the integrations list">
            Tally currently supports the following integrations:
          </Trans>
        </p>
        <ul>
          <li>
            <strong>e-conomic:</strong>{" "}
            <Trans context="Help page: e-conomic integration description">
              a Danish accounting platform for managing financial data.
            </Trans>
          </li>
          <li>
            <strong>Google Sheets:</strong>{" "}
            <Trans context="Help page: Google Sheets integration description">
              analyze your own spreadsheets alongside your other business data.
            </Trans>
          </li>
          <li>
            <strong>Intempus:</strong>{" "}
            <Trans context="Help page: Intempus integration description">
              a time tracking and workforce platform for reporting and
              operational insights.
            </Trans>
          </li>
          <li>
            <strong>Rackbeat:</strong>{" "}
            <Trans context="Help page: Rackbeat integration description">
              an inventory and order management platform for stock, purchasing,
              and sales insights.
            </Trans>
          </li>
          <li>
            <strong>Fenerum:</strong>{" "}
            <Trans context="Help page: Fenerum integration description">
              a subscription and invoicing platform for recurring-revenue and
              customer lifecycle analysis.
            </Trans>
          </li>
          <li>
            <strong>Business Central:</strong>{" "}
            <Trans context="Help page: Business Central integration description">
              Microsoft's ERP/accounting platform for general ledger reporting,
              account analysis, and customer/vendor insights.
            </Trans>
          </li>
        </ul>
      </section>

      <section>
        <h2>
          <Trans context="Help page section heading: pricing and trial">
            Pricing &amp; trial
          </Trans>
        </h2>
        <p>
          <Trans context="Help page: pricing and trial description">
            New users can try Tally for free before subscribing. Once your trial
            ends, a paid subscription unlocks unlimited questions, detailed
            reporting, advanced analysis, and all integrations. Visit our{" "}
            <a
              href="/support"
              onClick={(e) => {
                e.preventDefault();
                navigate("/support");
              }}
            >
              Support page
            </a>{" "}
            for details on pricing and to get in touch with our sales team.
          </Trans>
        </p>
      </section>

      <section>
        <h2>
          <Trans context="Help page section heading: getting started">
            Getting started
          </Trans>
        </h2>
        <p>
          <Trans context="Help page: how to create an account">
            To create an account, click the "Create an Account" button below the
            chat window on the Tally homepage. You can sign up and log in using
            your Google or Visma Connect account &mdash; no separate username or
            password required.
          </Trans>
        </p>
      </section>

      <section>
        <h2>
          <Trans context="Help page section heading: example questions">
            Example questions you can ask Tally
          </Trans>
        </h2>
        <ul>
          <li>
            <Trans context="Help page example question 1">
              What was my total turnover last month?
            </Trans>
          </li>
          <li>
            <Trans context="Help page example question 2">
              Can you list the non-booked entries on account 1010 for the 1st of
              January 2024?
            </Trans>
          </li>
          <li>
            <Trans context="Help page example question 3">
              List the 3 largest expenses in March 2024 on account 1000, with
              amounts and descriptions.
            </Trans>
          </li>
          <li>
            <Trans context="Help page example question 4">
              Draw me a graph of total costs per month for this year.
            </Trans>
          </li>
          <li>
            <Trans context="Help page example question 5">
              How many departments do we have?
            </Trans>
          </li>
        </ul>
      </section>

      <footer>
        <p>
          <Trans context="Help page footer: link to the support page">
            Still need help? Visit our{" "}
            <a
              href="/support"
              onClick={(e) => {
                e.preventDefault();
                navigate("/support");
              }}
            >
              Support page
            </a>{" "}
            for ways to reach us directly.
          </Trans>
        </p>
      </footer>
    </div>
  );
}
